import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { Loader2, GraduationCap, BookOpen } from "lucide-react";
import { useDeleteAssignment } from "@/hooks/useDeleteAssignment";
import { toast } from "sonner";
import type { Assignment } from "@/types/assignments";

interface DeleteAssignmentDialogProps {
  assignment: Assignment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function DeleteAssignmentDialog({
  assignment,
  open,
  onOpenChange,
  onSuccess,
}: DeleteAssignmentDialogProps) {
  const deleteAssignment = useDeleteAssignment();

  const handleDelete = async () => {
    if (!assignment) return;

    try {
      await deleteAssignment.mutateAsync({
        id: assignment.id,
        type: assignment.type,
      });

      toast.success("Assignment removed");
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error("Failed to delete assignment:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to remove assignment. Please try again."
      );
    }
  };

  if (!assignment) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove Assignment?</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to remove this assignment? This action cannot
            be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="my-4 p-4 rounded-lg border bg-muted/30 space-y-3">
          {/* User */}
          <div className="flex items-center gap-3">
            <UserAvatar
              avatarPath={assignment.user_avatar}
              fullName={assignment.user_name}
              className="h-10 w-10"
              thumbnailSize={64}
            />
            <div>
              <p className="font-medium">{assignment.user_name}</p>
              <p className="text-sm text-muted-foreground">Assigned user</p>
            </div>
          </div>

          {/* Item */}
          <div className="flex items-center gap-3 pt-2 border-t">
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
              {assignment.type === "curriculum" ? (
                <GraduationCap className="h-5 w-5 text-blue-600" />
              ) : (
                <BookOpen className="h-5 w-5 text-green-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{assignment.item_name}</p>
              <Badge
                variant="secondary"
                className={
                  assignment.type === "curriculum"
                    ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                    : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                }
              >
                {assignment.type === "curriculum" ? "Curriculum" : "Course"}
              </Badge>
            </div>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteAssignment.isPending}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={deleteAssignment.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {deleteAssignment.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Removing...
              </>
            ) : (
              "Delete"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
